import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const WatchlistManager = ({ onWatchlistChange }) => {
  const [activeTab, setActiveTab] = useState('my-lists');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [newListName, setNewListName] = useState('');

  const watchlists = [
    {
      id: 'default',
      name: 'Default Watchlist',
      symbols: ['RELIANCE', 'TCS', 'INFY', 'HDFCBANK', 'ICICIBANK'],
      type: 'stocks',
      isDefault: true,
      createdAt: '2024-01-15',
      updatedAt: '2024-01-20'
    },
    {
      id: 'crypto-main',
      name: 'Crypto Portfolio',
      symbols: ['BTC', 'ETH', 'BNB', 'ADA', 'DOT'],
      type: 'crypto',
      isDefault: false,
      createdAt: '2024-01-18',
      updatedAt: '2024-01-20'
    },
    {
      id: 'nifty50',
      name: 'Nifty 50',
      symbols: ['RELIANCE', 'TCS', 'HDFCBANK', 'INFY', 'HINDUNILVR', 'ICICIBANK', 'KOTAKBANK', 'BHARTIARTL', 'ITC', 'SBIN'],
      type: 'stocks',
      isDefault: false,
      createdAt: '2024-01-10',
      updatedAt: '2024-01-19'
    },
    {
      id: 'banking',
      name: 'Banking Sector',
      symbols: ['HDFCBANK', 'ICICIBANK', 'KOTAKBANK', 'SBIN', 'AXISBANK'],
      type: 'stocks',
      isDefault: false,
      createdAt: '2024-01-12',
      updatedAt: '2024-01-18'
    }
  ];

  const predefinedLists = [
    {
      id: 'nifty-next50',
      name: 'Nifty Next 50',
      description: 'Top 50 stocks after Nifty 50',
      symbolCount: 50,
      type: 'stocks'
    },
    {
      id: 'top-crypto',
      name: 'Top 20 Cryptocurrencies',
      description: 'Most traded cryptocurrencies by market cap',
      symbolCount: 20,
      type: 'crypto'
    },
    {
      id: 'it-sector',
      name: 'IT Sector',
      description: 'Information Technology companies',
      symbolCount: 15,
      type: 'stocks'
    }
  ];

  const filteredWatchlists = watchlists?.filter(list =>
    list?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    list?.symbols?.some(symbol => symbol?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
  );

  const handleCreateList = () => {
    if (newListName?.trim()) {
      // Create new watchlist logic here
      setIsCreatingList(false);
      setNewListName('');
    }
  };

  const handleDeleteList = (listId) => {
    // Delete watchlist logic here
    console.log('Deleting list:', listId);
  };

  const handleDuplicateList = (listId) => {
    // Duplicate watchlist logic here
    console.log('Duplicating list:', listId);
  };

  const getTypeIcon = (type) => {
    return type === 'crypto' ? 'Bitcoin' : 'TrendingUp';
  };

  const getTypeColor = (type) => {
    return type === 'crypto' ? 'text-warning' : 'text-primary';
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Watchlist Manager</h3>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setIsCreatingList(true)}
          >
            New List
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => setActiveTab('my-lists')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
              activeTab === 'my-lists' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            My Lists ({watchlists?.length})
          </button>
          <button
            onClick={() => setActiveTab('predefined')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-smooth ${
              activeTab === 'predefined' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Predefined
          </button>
        </div>
      </div>
      {/* Search */}
      <div className="p-4 border-b border-border">
        <Input
          type="search"
          placeholder="Search watchlists or symbols..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Content */}
      <div className="p-4">
        {/* Create New List Modal */}
        {isCreatingList && (
          <div className="bg-muted/50 border border-border rounded-lg p-4 mb-4">
            <h4 className="font-medium text-foreground mb-3">Create New Watchlist</h4>
            <div className="space-y-3">
              <Input
                label="Watchlist Name"
                value={newListName}
                onChange={(e) => setNewListName(e?.target?.value)}
                placeholder="Enter watchlist name"
              />
              <div className="flex space-x-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleCreateList}
                  disabled={!newListName?.trim()}
                >
                  Create
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsCreatingList(false);
                    setNewListName('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* My Lists Tab */}
        {activeTab === 'my-lists' && (
          <div className="space-y-3">
            {filteredWatchlists?.map((list) => (
              <div key={list?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-smooth">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon 
                        name={getTypeIcon(list?.type)} 
                        size={16} 
                        className={getTypeColor(list?.type)} 
                      />
                      <h4 className="font-medium text-foreground">{list?.name}</h4>
                      {list?.isDefault && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground mb-2">
                      {list?.symbols?.length} symbols • Updated {new Date(list.updatedAt)?.toLocaleDateString()}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {list?.symbols?.slice(0, 5)?.map((symbol) => (
                        <span
                          key={symbol}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground"
                        >
                          {symbol}
                        </span>
                      ))}
                      {list?.symbols?.length > 5 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground">
                          +{list?.symbols?.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleDuplicateList(list?.id)}
                      className="p-2 text-muted-foreground hover:text-foreground transition-smooth"
                      title="Duplicate"
                    >
                      <Icon name="Copy" size={16} />
                    </button>
                    <button
                      className="p-2 text-muted-foreground hover:text-foreground transition-smooth"
                      title="Edit"
                    >
                      <Icon name="Edit" size={16} />
                    </button>
                    {!list?.isDefault && (
                      <button
                        onClick={() => handleDeleteList(list?.id)}
                        className="p-2 text-muted-foreground hover:text-error transition-smooth"
                        title="Delete"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Predefined Lists Tab */}
        {activeTab === 'predefined' && (
          <div className="space-y-3">
            {predefinedLists?.map((list) => (
              <div key={list?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-smooth">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon 
                        name={getTypeIcon(list?.type)} 
                        size={16} 
                        className={getTypeColor(list?.type)} 
                      />
                      <h4 className="font-medium text-foreground">{list?.name}</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{list?.description}</p>
                    <div className="text-sm text-muted-foreground">
                      {list?.symbolCount} symbols
                    </div>
                  </div>
                  <div className="ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Add to My Lists
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WatchlistManager;